/**
 * useShapePropertyEditor
 *
 * ShapePropertyManager와 ShapeMiniToolbar에서 공유하는 도형 속성 편집 로직.
 *
 * 변경 이력 (shape-conversion-simplify):
 * - RenderNode가 storage-shape 필드를 직접 보유하므로 `renderNodeToStorage` /
 *   `convertToAppearance` 변환 단계가 제거됐다. `currentProps`는 선택된 RenderNode
 *   자체를 그대로 노출하고, `handlePropChange`는 store 내 노드의 top-level 필드를
 *   shallow update 한다. 편집된 노드만 새 참조로 교체하여 RFC 어댑터의 WeakMap
 *   캐시 hit 을 최대한 보존한다 (decision.md #4 — structuredClone 금지).
 * - Cancel/ESC 롤백은 `ShapePropertyManager.tsx`의 `shapesSnapshotRef`(JSON clone)
 *   패턴이 그대로 처리하므로 본 훅은 로컬 상태를 두지 않는다 (decision.md #3).
 */

import { useCallback, useMemo } from 'react';
import { useStore } from 'zustand';
import { ShapeType } from '@/types/commontypes';
import type { RenderNode, ShapePropertyInputDef } from '@/types/commontypes';
import { ShapeProperties, ShapePropertyDefKey } from '@/utils/shapeproperty';
import { defaultShapePropertyValues } from '@/utils/defaultShapePropertyValues';
import { useChartShapes } from '../contexts';
import { useGlobalShapeSelector } from '../contexts/GlobalShapesContext';

export function useShapePropertyEditor() {
  const shapeTypeToDraw = useGlobalShapeSelector(s => s.shapeTypeToDraw);
  const { shapeStore } = useChartShapes();
  const renderNodes = useStore(shapeStore, s => s.renderNodes);
  const setRenderNodes = useStore(shapeStore, s => s.setRenderNodes);
  const selectedShape = useGlobalShapeSelector(s => s.selectedShape);
  const updateLastShapeProperty = useGlobalShapeSelector(s => s.updateLastShapeProperty);

  // ── 1. renderNodes에서 RenderNode 조회 ──
  const currentNode = useMemo((): RenderNode | undefined => {
    if (!selectedShape) return undefined;
    const { chartId, type, uuid } = selectedShape;
    if (!chartId || !type || !uuid) return undefined;
    const shapes = renderNodes[chartId]?.[type];
    if (!shapes) return undefined;
    return (shapes as RenderNode[]).find(s => s.uuid === uuid);
  }, [renderNodes, selectedShape]);

  // ── 2. currentProps: 변환 없이 노드 자체를 노출 ──
  // RenderNode variant가 이미 storage-shape 필드를 직접 보유한다 (SubTask 1+2).
  // RuntimeFields(hidden, locked, node 등)는 DynamicRenderer가 properties 정의에
  // 명시된 key 만 읽기 때문에 자연스럽게 무시된다.
  const currentProps = useMemo((): Record<string, unknown> | null => {
    if (currentNode) {
      // Measure 등 저장 비대상 도형은 속성 편집 불가
      if (currentNode.type === ShapeType.MEASURE) {
        return null;
      }
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      return currentNode as unknown as Record<string, unknown>;
    }
    // 도형이 renderNodes에 아직 없을 때 (그리기 시작 직후 또는 툴바 선택만 한 상태)
    const type = selectedShape?.type ?? shapeTypeToDraw;
    if (type && type in defaultShapePropertyValues) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      return defaultShapePropertyValues[type] as Record<string, unknown>;
    }
    return null;
  }, [currentNode, selectedShape?.type, shapeTypeToDraw]);

  // ── 3. 속성 정의 조회 (miniOnly 필터 지원) ──
  const resolveProperties = useCallback((type: ShapeType | undefined, miniOnly?: boolean): ShapePropertyInputDef[] => {
    if (!type) return [];
    const defKey = ShapePropertyDefKey[type];
    if (!defKey) return [];
    const allDefs = ShapeProperties[defKey] || [];
    return miniOnly ? allDefs.filter(def => def.showInMiniToolbar) : allDefs;
  }, []);

  // ── 4. Props 변경 → store 노드 필드 shallow update ──
  // 편집된 노드만 새 객체로 교체하고, 같은 type의 다른 노드는 배열 내 참조를
  // 유지한다 → RFC 어댑터의 WeakMap cache hit 보존 (decision.md #4).
  const handlePropChange = useCallback(
    (name: string, newValue: unknown) => {
      if (!selectedShape) return;
      const { chartId, type, uuid } = selectedShape;
      if (!chartId || !type || !uuid) return;

      setRenderNodes(prev => {
        const chartShapes = prev[chartId];
        if (!chartShapes) return prev;
        const typeShapes = chartShapes[type] as RenderNode[] | undefined;
        if (!typeShapes) return prev;

        let updatedNode: RenderNode | undefined;
        let changed = false;
        const nextTypeShapes = typeShapes.map(n => {
          if (n.uuid !== uuid) return n;

          // 동일 값 재할당이면 새 객체를 만들지 않는다 — color picker 연타 등에서
          // 불필요한 setRenderNodes/save 트리거 + WeakMap 캐시 무효화를 막는다.
          if ((n as unknown as Record<string, unknown>)[name] === newValue) {
            return n;
          }

          changed = true;
          updatedNode = {
            ...n,
            [name]: newValue,
          };
          return updatedNode;
        });

        if (!changed || !updatedNode) return prev;

        // dirty set에 추가하여 delta 저장 트리거
        shapeStore.getState().markDirty(uuid);

        // localStorage 영속화: 마지막 편집 값을 해당 type의 기본값으로 기억한다.
        // RuntimeFields가 섞여 있어도 다음 그리기 시점에 필요한 key만 참조된다.
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        updateLastShapeProperty(type, updatedNode as never);

        return {
          ...prev,
          [chartId]: {
            ...chartShapes,
            [type]: nextTypeShapes,
          },
        };
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- shapeStore는 Context Provider 수명 동안 불변 참조이며 .getState()로 호출하므로 deps 불필요
    [selectedShape, setRenderNodes, updateLastShapeProperty],
  );

  return { currentNode, currentProps, resolveProperties, handlePropChange };
}
